class ManageAvatarController {
  constructor($uibModalInstance, $http, $rootScope, toastr, AuthenticationService, $timeout, $scope) {
    "ngInject";

    this.$uibModalInstance = $uibModalInstance;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.authenticationService = AuthenticationService;
    this.$timeout = $timeout;
    this.$scope = $scope;

    this.currentAvatarUrl = $uibModalInstance.currentAvatarUrl || null;
    this.hasExistingAvatar = !!$uibModalInstance.hasExistingAvatar;
    
    this.uploadedImage = null;
    this.uploadedImageFile = null;
    this.imageLoaded = false;
    this.rotation = 0;
    this.scale = 1;
    this.cropX = 0;
    this.cropY = 0;
    this.cropSize = 200; // Default crop size
    
    // Track original state to detect changes
    this.originalRotation = 0;
    this.originalScale = 1;
    this.originalCropX = 0;
    this.originalCropY = 0;
    this.originalCropSize = 200;
    this.originalImageSrc = null; // Track the original image source
    this.isNewImage = false; // Track if user uploaded a new image file
    this.originalStateSaved = false; // Track if we've saved the original state
    
    this.previewCanvas = null;
    this.sourceCanvas = null;
    this.sourceCtx = null;
    this.previewCtx = null;
    this.imageCanvas = null; // Separate canvas for the actual image (without overlay)
    this.imageCtx = null;
    
    this.fileInputId = `avatar-upload-${Date.now()}`;
    this.sourceCanvasId = `avatar-source-${Date.now()}`;
    this.previewCanvasId = `avatar-preview-${Date.now()}`;
    
    this.boundFileChangeHandler = this.onFileChange.bind(this);
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.initialCropX = 0;
    this.initialCropY = 0;
  }

  $onInit() {
    this.$timeout(() => {
      this.fileInputEl = document.getElementById(this.fileInputId);
      if (this.fileInputEl) {
        this.fileInputEl.addEventListener('change', this.boundFileChangeHandler);
      }
      
      // Create a separate canvas for the image (without overlay) - this is what we'll crop from
      // This can be created immediately since it's not in the DOM
      this.imageCanvas = document.createElement('canvas');
      this.imageCtx = this.imageCanvas.getContext('2d');
      
      // Canvas elements are conditionally rendered with ng-if="$ctrl.imageLoaded"
      // So we don't initialize them here - they'll be initialized in loadImage() 
      // after the image is loaded and the DOM is updated
      
      // Load existing avatar if available
      // Only try to load if we have a valid URL (not null/undefined)
      if (this.currentAvatarUrl && this.currentAvatarUrl.trim() !== '' && this.hasExistingAvatar) {
        // Check if URL looks valid (contains http/https and doesn't have undefined)
        if (this.currentAvatarUrl.includes('http') && !this.currentAvatarUrl.includes('undefined')) {
          console.log('Loading existing avatar from URL:', this.currentAvatarUrl);
          this.loadImageFromUrl(this.currentAvatarUrl);
        } else {
          console.warn('Invalid avatar URL:', this.currentAvatarUrl);
        }
      } else {
        console.log('No existing avatar to load. hasExistingAvatar:', this.hasExistingAvatar, 'currentAvatarUrl:', this.currentAvatarUrl);
      }
    }, 100);
  }

  $onDestroy() {
    if (this.fileInputEl) {
      this.fileInputEl.removeEventListener('change', this.boundFileChangeHandler);
    }
  }

  onFileChange(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.toastr.error('Please select an image file.', 'Invalid File');
      return;
    }

    this.uploadedImageFile = file;
    this.isNewImage = true; // Mark that a new image was uploaded
    const reader = new FileReader();
    reader.onload = (e) => {
      this.loadImage(e.target.result);
    };
    reader.readAsDataURL(file);
  }

  loadImageFromUrl(url) {
    if (!url) {
      return;
    }
    
    // Use $http to load the image with proper authentication handling
    // Use arraybuffer responseType and convert to blob for AngularJS 1.x compatibility
    this.$http.get(url, {
      responseType: 'arraybuffer',
      withCredentials: true,
      headers: {
        'Accept': 'image/*'
      }
    })
    .then((response) => {
      // Convert arraybuffer to blob, then to data URL
      // Get content type from headers (AngularJS 1.x uses lowercase header names)
      let contentType = 'image/jpeg';
      if (response.headers) {
        contentType = response.headers('content-type') || response.headers('Content-Type') || 'image/jpeg';
      }
      const blob = new Blob([response.data], { type: contentType });
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('Image loaded successfully, calling loadImage with data URL');
        this.loadImage(e.target.result);
      };
      reader.onerror = () => {
        this.toastr.warning('Could not process existing avatar. You can upload a new one.', 'Image Load Error');
      };
      reader.readAsDataURL(blob);
    })
    .catch((error) => {
      // If loading fails (CORS, authentication, etc.), just show a message
      // User can still upload a new image
      console.warn('Could not load existing avatar:', error);
      // Don't show error toast if it's just a 404 or similar - user might not have an avatar
      // Also don't show if it's a redirect (status 0 or no status) which indicates CORS/auth issues
      if (error.status && error.status !== 404 && error.status !== 0) {
        this.toastr.warning('Could not load existing avatar. You can upload a new one.', 'Image Load Error');
      }
      // Don't prevent the modal from being usable - user can still upload
    });
  }

  loadImage(imageSrc) {
    console.log('loadImage called with imageSrc type:', typeof imageSrc, 'length:', imageSrc ? imageSrc.length : 0);
    const img = new Image();
    img.onload = () => {
      console.log('Image object loaded, dimensions:', img.width, 'x', img.height);
      this.uploadedImage = img;
      this.rotation = 0;
      this.scale = 1;
      
      // Set imageLoaded to true, which will trigger ng-if to render the canvas
      this.imageLoaded = true;
      console.log('imageLoaded set to true, waiting for DOM update...');
      
      // Wait for Angular to render the canvas elements after ng-if becomes true
      // Use $timeout to ensure we're in the next digest cycle
      this.$timeout(() => {
        console.log('DOM update timeout fired, initializing canvas elements...');
        // Re-initialize canvas references now that they exist in the DOM
        this.sourceCanvas = document.getElementById(this.sourceCanvasId);
        this.previewCanvas = document.getElementById(this.previewCanvasId);
        
        if (this.sourceCanvas) {
          this.sourceCtx = this.sourceCanvas.getContext('2d');
        }
        if (this.previewCanvas) {
          this.previewCtx = this.previewCanvas.getContext('2d');
          // Set preview canvas size (circular avatar)
          this.previewCanvas.width = 200;
          this.previewCanvas.height = 200;
        }
        
        // Now draw the image
        if (this.sourceCanvas && this.sourceCtx) {
          this.drawImage();
          
          // Calculate initial crop position (center the canvas)
          const minDimension = Math.min(this.sourceCanvas.width, this.sourceCanvas.height);
          this.cropSize = Math.min(minDimension * 0.8, 400);
          this.cropX = (this.sourceCanvas.width - this.cropSize) / 2;
          this.cropY = (this.sourceCanvas.height - this.cropSize) / 2;
          
          this.drawImage();
          this.updatePreview();
          
          // Store original state after everything is set up
          // This captures the initial state whether it's a new image or existing avatar
          if (!this.originalStateSaved) {
            // Small delay to ensure all values are set
            this.$timeout(() => {
              this.saveOriginalState();
            }, 50);
          }
        }
      }, 100); // Give Angular time to render the DOM
    };
    img.src = imageSrc;
  }
  
  saveOriginalState() {
    this.originalRotation = this.rotation;
    this.originalScale = this.scale;
    this.originalCropX = this.cropX;
    this.originalCropY = this.cropY;
    this.originalCropSize = this.cropSize;
    this.originalStateSaved = true;
    
    // If this is a new image upload, we consider it changed from the start
    // But we still save the state so that if user reverts changes, we can detect that
    console.log('Original state saved:', {
      rotation: this.originalRotation,
      scale: this.originalScale,
      cropX: this.originalCropX,
      cropY: this.originalCropY,
      cropSize: this.originalCropSize,
      isNewImage: this.isNewImage
    });
  }
  
  hasChanges() {
    if (!this.imageLoaded) {
      return false;
    }
    
    // If original state hasn't been saved yet, no changes (still loading)
    if (!this.originalStateSaved) {
      return false;
    }
    
    // If a new image file was uploaded, there are always changes
    // (even if user hasn't modified it, uploading a new file is a change)
    if (this.isNewImage) {
      return true;
    }
    
    // Check if any transformation changed (with small tolerance for floating point errors)
    const rotationChanged = Math.abs(this.rotation - this.originalRotation) > 0.1;
    const scaleChanged = Math.abs(this.scale - this.originalScale) > 0.01;
    const cropXChanged = Math.abs(this.cropX - this.originalCropX) > 1;
    const cropYChanged = Math.abs(this.cropY - this.originalCropY) > 1;
    const cropSizeChanged = Math.abs(this.cropSize - this.originalCropSize) > 1;
    
    const hasChanges = rotationChanged || scaleChanged || cropXChanged || cropYChanged || cropSizeChanged;
    
    return hasChanges;
  }

  drawImage() {
    // Ensure canvas elements are initialized
    if (!this.sourceCanvas) {
      this.sourceCanvas = document.getElementById(this.sourceCanvasId);
      if (this.sourceCanvas) {
        this.sourceCtx = this.sourceCanvas.getContext('2d');
      }
    }
    if (!this.previewCanvas) {
      this.previewCanvas = document.getElementById(this.previewCanvasId);
      if (this.previewCanvas) {
        this.previewCtx = this.previewCanvas.getContext('2d');
        this.previewCanvas.width = 200;
        this.previewCanvas.height = 200;
      }
    }
    
    if (!this.sourceCanvas || !this.sourceCtx || !this.uploadedImage || !this.imageCanvas || !this.imageCtx) {
      return;
    }

    // Calculate scaled dimensions
    const scaledWidth = this.uploadedImage.width * this.scale;
    const scaledHeight = this.uploadedImage.height * this.scale;
    
    // Calculate canvas size to fit rotated/scaled image
    const angle = (this.rotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(angle));
    const sin = Math.abs(Math.sin(angle));
    const canvasWidth = scaledWidth * cos + scaledHeight * sin;
    const canvasHeight = scaledWidth * sin + scaledHeight * cos;
    
    // Set canvas sizes (both source and image canvas)
    const finalWidth = Math.max(canvasWidth, 400);
    const finalHeight = Math.max(canvasHeight, 400);
    this.sourceCanvas.width = finalWidth;
    this.sourceCanvas.height = finalHeight;
    this.imageCanvas.width = finalWidth;
    this.imageCanvas.height = finalHeight;

    // Clear both canvases
    this.sourceCtx.clearRect(0, 0, this.sourceCanvas.width, this.sourceCanvas.height);
    this.imageCtx.clearRect(0, 0, this.imageCanvas.width, this.imageCanvas.height);

    // Draw the image on both canvases
    const drawImageToCanvas = (ctx, canvas) => {
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(angle);
      ctx.scale(this.scale, this.scale);
      ctx.drawImage(
        this.uploadedImage,
        -this.uploadedImage.width / 2,
        -this.uploadedImage.height / 2
      );
      ctx.restore();
    };

    // Draw image to image canvas (for cropping)
    drawImageToCanvas(this.imageCtx, this.imageCanvas);
    
    // Draw image to source canvas (for display)
    drawImageToCanvas(this.sourceCtx, this.sourceCanvas);

    // Ensure crop area is within bounds
    this.cropX = Math.max(0, Math.min(this.sourceCanvas.width - this.cropSize, this.cropX));
    this.cropY = Math.max(0, Math.min(this.sourceCanvas.height - this.cropSize, this.cropY));

    // Draw crop overlay on source canvas (for display only)
    this.drawCropOverlay();
  }

  drawCropOverlay() {
    if (!this.sourceCtx) return;

    const ctx = this.sourceCtx;
    const width = this.sourceCanvas.width;
    const height = this.sourceCanvas.height;

    // Draw dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, width, height);

    // Clear the crop area (circular)
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(this.cropX + this.cropSize / 2, this.cropY + this.cropSize / 2, this.cropSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';

    // Draw crop circle border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(this.cropX + this.cropSize / 2, this.cropY + this.cropSize / 2, this.cropSize / 2, 0, Math.PI * 2);
    ctx.stroke();

    // Draw guide lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    // Horizontal line
    ctx.beginPath();
    ctx.moveTo(this.cropX + this.cropSize / 2, this.cropY);
    ctx.lineTo(this.cropX + this.cropSize / 2, this.cropY + this.cropSize);
    ctx.stroke();
    // Vertical line
    ctx.beginPath();
    ctx.moveTo(this.cropX, this.cropY + this.cropSize / 2);
    ctx.lineTo(this.cropX + this.cropSize, this.cropY + this.cropSize / 2);
    ctx.stroke();
  }

  updatePreview() {
    // Ensure canvas elements are initialized
    if (!this.previewCanvas) {
      this.previewCanvas = document.getElementById(this.previewCanvasId);
      if (this.previewCanvas) {
        this.previewCtx = this.previewCanvas.getContext('2d');
        this.previewCanvas.width = 200;
        this.previewCanvas.height = 200;
      }
    }
    
    // imageCanvas should be created in $onInit, but ensure it exists
    if (!this.imageCanvas) {
      this.imageCanvas = document.createElement('canvas');
      this.imageCtx = this.imageCanvas.getContext('2d');
    }
    
    if (!this.previewCanvas || !this.previewCtx || !this.uploadedImage || !this.imageCanvas || !this.imageCtx) {
      return;
    }

    // Clear preview
    this.previewCtx.clearRect(0, 0, this.previewCanvas.width, this.previewCanvas.height);

    // Create a temporary canvas to extract the cropped region from the image canvas (without overlay)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.cropSize;
    tempCanvas.height = this.cropSize;
    const tempCtx = tempCanvas.getContext('2d');

    // Extract the cropped region from the image canvas (which has rotation/scale but no overlay)
    tempCtx.drawImage(
      this.imageCanvas,
      this.cropX,
      this.cropY,
      this.cropSize,
      this.cropSize,
      0,
      0,
      this.cropSize,
      this.cropSize
    );

    // Draw circular clipping on preview
    this.previewCtx.save();
    this.previewCtx.beginPath();
    this.previewCtx.arc(
      this.previewCanvas.width / 2,
      this.previewCanvas.height / 2,
      this.previewCanvas.width / 2,
      0,
      Math.PI * 2
    );
    this.previewCtx.clip();
    this.previewCtx.drawImage(tempCanvas, 0, 0, this.previewCanvas.width, this.previewCanvas.height);
    this.previewCtx.restore();
  }

  rotateLeft() {
    this.rotation -= 90;
    if (this.rotation < 0) {
      this.rotation += 360;
    }
    this.drawImage();
    this.updatePreview();
  }

  rotateRight() {
    this.rotation += 90;
    if (this.rotation >= 360) {
      this.rotation -= 360;
    }
    this.drawImage();
    this.updatePreview();
  }

  zoomIn() {
    this.scale = Math.min(this.scale + 0.1, 3);
    this.drawImage();
    this.updatePreview();
  }

  zoomOut() {
    this.scale = Math.max(this.scale - 0.1, 0.5);
    this.drawImage();
    this.updatePreview();
  }

  onCanvasMouseDown(event) {
    if (!this.imageLoaded || !this.sourceCanvas) return;
    
    const rect = this.sourceCanvas.getBoundingClientRect();
    const scaleX = this.sourceCanvas.width / rect.width;
    const scaleY = this.sourceCanvas.height / rect.height;
    
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    
    // Check if click is within crop area
    const cropCenterX = this.cropX + this.cropSize / 2;
    const cropCenterY = this.cropY + this.cropSize / 2;
    const distance = Math.sqrt(
      Math.pow(x - cropCenterX, 2) +
      Math.pow(y - cropCenterY, 2)
    );
    
    if (distance <= this.cropSize / 2) {
      this.isDragging = true;
      this.dragStartX = x;
      this.dragStartY = y;
      this.initialCropX = this.cropX;
      this.initialCropY = this.cropY;
    }
  }

  onCanvasMouseMove(event) {
    if (!this.isDragging || !this.imageLoaded || !this.sourceCanvas) return;
    
    const rect = this.sourceCanvas.getBoundingClientRect();
    const scaleX = this.sourceCanvas.width / rect.width;
    const scaleY = this.sourceCanvas.height / rect.height;
    
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    
    const deltaX = x - this.dragStartX;
    const deltaY = y - this.dragStartY;
    
    // Update crop position
    this.cropX = Math.max(0, Math.min(this.sourceCanvas.width - this.cropSize, this.initialCropX + deltaX));
    this.cropY = Math.max(0, Math.min(this.sourceCanvas.height - this.cropSize, this.initialCropY + deltaY));
    
    this.drawImage();
    this.updatePreview();
    // No need for $apply() - ng-mousemove already triggers Angular's digest cycle
  }

  onCanvasMouseUp() {
    this.isDragging = false;
  }

  resizeCrop(delta) {
    const newSize = Math.max(50, Math.min(Math.min(this.sourceCanvas.width, this.sourceCanvas.height), this.cropSize + delta));
    this.cropSize = newSize;
    
    // Keep crop within bounds
    this.cropX = Math.max(0, Math.min(this.sourceCanvas.width - this.cropSize, this.cropX));
    this.cropY = Math.max(0, Math.min(this.sourceCanvas.height - this.cropSize, this.cropY));
    
    this.drawImage();
    this.updatePreview();
  }

  cropSizeUp() {
    this.resizeCrop(10);
  }

  cropSizeDown() {
    this.resizeCrop(-10);
  }

  getCroppedImageBlob() {
    if (!this.uploadedImage || !this.imageCanvas) {
      return null;
    }

    // Create a canvas for the final cropped image (square, 400x400)
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');

    // Extract the cropped region from the image canvas (which has rotation/scale but no overlay)
    // and scale it to 400x400
    ctx.drawImage(
      this.imageCanvas,
      this.cropX,
      this.cropY,
      this.cropSize,
      this.cropSize,
      0,
      0,
      400,
      400
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  }

  async saveAvatar() {
    if (!this.imageLoaded) {
      this.toastr.error('Please upload an image first.', 'No Image');
      return;
    }
    
    if (!this.hasChanges()) {
      this.toastr.warning('No changes to save.', 'No Changes');
      return;
    }

    try {
      const blob = await this.getCroppedImageBlob();
      if (!blob) {
        this.toastr.error('Could not process image.', 'Error');
        return;
      }

      const fd = new FormData();
      fd.append('file', blob, 'avatar.jpg');

      const config = {
        headers: { 'Content-Type': undefined },
        withCredentials: true,
        transformRequest: angular.identity,
      };

      this.$http.post(`${this.$rootScope.apiHost}/api/users/me/avatar`, fd, config)
        .then(() => this.authenticationService.refreshUser())
        .then(() => {
          this.toastr.success('Avatar updated successfully.', 'Avatar Updated');
          this.$rootScope.profilePicTimeStamp = (new Date()).getTime();
          // Reset change tracking after successful save
          // The current state becomes the new "original" state
          this.saveOriginalState();
          this.isNewImage = false; // No longer a "new" image after it's saved
          this.$uibModalInstance.close(true);
        })
        .catch(() => {
          this.toastr.error('Could not update your avatar.', 'ERROR');
        });
    } catch (error) {
      this.toastr.error('Could not process image.', 'Error');
    }
  }

  deleteAvatar() {
    this.$http.delete(`${this.$rootScope.apiHost}/api/users/me/avatar`, { withCredentials: true })
      .then(() => this.authenticationService.refreshUser())
      .then(() => {
        this.toastr.success('Avatar deleted successfully.', 'Avatar Deleted');
        this.$rootScope.profilePicTimeStamp = (new Date()).getTime();
        this.$uibModalInstance.close(true);
      })
      .catch(() => {
        this.toastr.error('Could not delete your avatar.', 'ERROR');
      });
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }

  selectFile() {
    if (this.fileInputEl) {
      this.fileInputEl.click();
    }
  }
}

export default ManageAvatarController;




// WEBPACK FOOTER //
// ./client/pages/profile/modals/manage-avatar/manage-avatar.controller.js